import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
// `?worker&url` and not `?url`: the plain form copies @ffmpeg's worker source
// VERBATIM into the build, and that file still carries `import ... from
// "./const.js"` / `"./errors.js"` — siblings that are never emitted beside it.
// Vite's dev server resolves them out of node_modules, so it only broke once
// built: in production the worker 404'd on its own imports and every video tool
// died at load. `?worker&url` compiles the worker first, inlining those two.
import ffmpegWorkerURL from '@ffmpeg/ffmpeg/worker?worker&url';
import coreURL from '@ffmpeg/core?url';
import wasmURL from '@ffmpeg/core/wasm?url';

export { formatFileSize } from '../../pdf/compress/compressPdfEngine';

let ffmpegInstance = null;
let ffmpegLoadPromise = null;

export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
const WORKERFS_INPUT_THRESHOLD = 32 * 1024 * 1024;
const WORKERFS_MOUNT_POINT = '/workerfs-input';

export const VIDEO_SIZE_PRESETS = [
    { value: 'original', maxDim: null },
    { value: '1080', maxDim: 1920 },
    { value: '720', maxDim: 1280 },
    { value: '480', maxDim: 854 },
    { value: '360', maxDim: 640 },
];

function getVideoInputName(file) {
    return `input${file.name.substring(file.name.lastIndexOf('.'))}`;
}

export async function prepareVideoInput(ffmpeg, file) {
    const inputName = getVideoInputName(file);

    if (file.size >= WORKERFS_INPUT_THRESHOLD) {
        const mounted = await ffmpeg.mount(
            'WORKERFS',
            { blobs: [{ name: inputName, data: file }] },
            WORKERFS_MOUNT_POINT,
        );
        if (mounted) {
            return { inputPath: `${WORKERFS_MOUNT_POINT}/${inputName}`, useWorkerFs: true };
        }
    }

    await ffmpeg.writeFile(inputName, await fetchFile(file));
    return { inputPath: inputName, useWorkerFs: false };
}

export async function cleanupVideoInput(ffmpeg, { inputPath, useWorkerFs }) {
    if (useWorkerFs) {
        try { await ffmpeg.unmount(WORKERFS_MOUNT_POINT); } catch (_) { /* ignore */ }
        return;
    }
    try { await ffmpeg.deleteFile(inputPath); } catch (_) { /* ignore */ }
}

export function resetFFmpeg() {
    if (ffmpegInstance) {
        try { ffmpegInstance.terminate(); } catch (_) { /* ignore */ }
        ffmpegInstance = null;
    }
    ffmpegLoadPromise = null;
}

async function createLoadedFFmpeg() {
    const ffmpeg = new FFmpeg();
    const classWorkerURL = new URL(ffmpegWorkerURL, import.meta.url).toString();
    const esmBase = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';

    try {
        await ffmpeg.load({
            classWorkerURL,
            coreURL: await toBlobURL(`${esmBase}/ffmpeg-core.js`, 'text/javascript'),
            wasmURL: await toBlobURL(`${esmBase}/ffmpeg-core.wasm`, 'application/wasm'),
        });
        return ffmpeg;
    } catch (_) {
        await ffmpeg.load({
            classWorkerURL,
            coreURL: await toBlobURL(coreURL, 'text/javascript'),
            wasmURL: await toBlobURL(wasmURL, 'application/wasm'),
        });
        return ffmpeg;
    }
}

export async function loadFFmpeg() {
    if (ffmpegInstance?.loaded) return ffmpegInstance;
    if (ffmpegLoadPromise) return ffmpegLoadPromise;

    ffmpegLoadPromise = (async () => {
        const ffmpeg = await createLoadedFFmpeg();
        ffmpegInstance = ffmpeg;
        return ffmpegInstance;
    })();

    try {
        return await ffmpegLoadPromise;
    } catch (error) {
        ffmpegLoadPromise = null;
        ffmpegInstance = null;
        throw error;
    }
}

/** Warm up FFmpeg WASM while the user picks files/settings. */
export function preloadFFmpeg() {
    return loadFFmpeg().catch(() => {});
}

export function getVideoDuration(file) {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            const dur = video.duration;
            URL.revokeObjectURL(video.src);
            resolve(dur && Number.isFinite(dur) ? dur : 0);
        };
        video.onerror = () => {
            URL.revokeObjectURL(video.src);
            resolve(0);
        };
        video.src = URL.createObjectURL(file);
    });
}

export function isSupportedVideoFile(file) {
    if (!file) return false;
    if (file.type?.startsWith('video/')) return true;
    return /\.(mp4|mov|avi|mkv|webm|m4v|wmv|flv|3gp)$/i.test(file.name || '');
}

export function compressedVideoName(fileName) {
    const base = (fileName || 'video').replace(/\.[^.]+$/, '');
    return `compressed-${base}.mp4`;
}

/**
 * @param {File} file
 * @param {{ quality?: number, maxDim?: number|null }} options
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Blob>}
 */
export async function compressVideo(file, options = {}, onProgress = () => {}) {
    let ffmpeg;
    let videoInput = null;
    const outputName = 'output.mp4';

    try {
        const { quality = 70, maxDim = null } = options;
        ffmpeg = await loadFFmpeg();

        const duration = await getVideoDuration(file);
        videoInput = await prepareVideoInput(ffmpeg, file);

        const progressHandler = ({ progress }) => {
            onProgress(Math.round(Math.min(progress * 100, 100)));
        };
        ffmpeg.on('progress', progressHandler);

        const audioBitrate = 96000;
        const targetRatio = Math.max(0.05, (quality / 100) * 0.95);

        let videoBitrateKbps = Math.max(200, Math.floor(800 * (quality / 50)));
        if (duration > 0) {
            const originalBitsPerSecond = (file.size * 8) / duration;
            const targetBitsPerSecond = originalBitsPerSecond * targetRatio;
            videoBitrateKbps = Math.max(100, Math.floor((targetBitsPerSecond - audioBitrate) / 1000));
        }

        let scaleFilter;
        if (maxDim) {
            const maxH = Math.round(maxDim * 9 / 16);
            scaleFilter = `scale=min(${maxDim}\\,iw):min(${maxH}\\,ih):force_original_aspect_ratio=decrease:force_divisible_by=2`;
        } else {
            scaleFilter = 'scale=trunc(iw/2)*2:trunc(ih/2)*2';
        }

        const encodePreset = file.size > 100 * 1024 * 1024 ? 'fast' : 'medium';
        const execTimeout = Math.max(180000, Math.ceil((duration || 60) * 4000));

        const exitCode = await ffmpeg.exec([
            '-i', videoInput.inputPath,
            '-vcodec', 'libx264',
            '-b:v', `${videoBitrateKbps}k`,
            '-maxrate', `${Math.floor(videoBitrateKbps * 1.5)}k`,
            '-bufsize', `${Math.floor(videoBitrateKbps * 2)}k`,
            '-preset', encodePreset,
            '-pix_fmt', 'yuv420p',
            '-vf', scaleFilter,
            '-acodec', 'aac',
            '-b:a', '96k',
            '-movflags', '+faststart',
            outputName,
        ], execTimeout);

        ffmpeg.off('progress', progressHandler);

        if (exitCode !== 0) {
            try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }
            throw new Error('ENCODE_FAILED');
        }

        const data = await ffmpeg.readFile(outputName);
        try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }

        if (!data || data.length === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        return new Blob([data], { type: 'video/mp4' });
    } catch (error) {
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (error?.message === 'ENCODE_FAILED' || error?.message === 'EMPTY_OUTPUT') {
            throw error;
        }
        throw new Error('COMPRESS_FAILED');
    } finally {
        if (ffmpeg && videoInput) {
            await cleanupVideoInput(ffmpeg, videoInput);
        }
    }
}
